import { FormGroup } from '@blueprintjs/core'
import classNames from 'classnames'
import React, { Component, RefObject } from 'react'
import ReactQuill, { Mixin as ReactQuillMixin } from 'react-quill'
import { connect } from 'react-redux'

import { HtmlToolbar } from 'modules/forms/components/html-toolbar'
import { DispatchProp } from 'modules/store'

import { SyncIndicator } from './sync-indicator'
import { FieldAdapterProps, HtmlEditorProps } from './types'
import { createHelperText, createLabelWithId, createRequiredLabel, getIntent, identity } from './utils'

import { isBlank, isPresent } from '../../app/shared'
import * as fromStore from '../../app/shared/store'

import { removalOfNotSupportedCharacters } from 'app/aspire/common/util/unsupported-chars-util'
import './html-editor-adapter.scss'

type Props = FieldAdapterProps<HtmlEditorProps> & DispatchProp

const enhance = connect()

export class HtmlEditorAdapter extends Component<Props> {
  private quillEditor: RefObject<ReactQuill & ReactQuillMixin>

  constructor(props: Props) {
    super(props)
    this.quillEditor = React.createRef<ReactQuill & ReactQuillMixin>()
  }

  render() {
    const {
      input,
      meta,
      label,
      placeholder,
      helperText,
      disabled = false,
      inline,
      required,
      intent,
      enableSyncControl,
      translate = identity,
      isSmallEditor = false,
      editorId = 'default',
      hasMoreLines = false,
      // tslint:disable-next-line:no-empty
      onSyncChange = (fieldName: string, newValue: boolean) => {},
    } = this.props
    const { id, labelText } = createLabelWithId(input, label)
    const intentType = getIntent(meta, intent)
    const helperTextMessage = createHelperText(meta, helperText, translate)
    const labelInfo = createRequiredLabel(required, translate)
    const { value } = input
    const reqLabelMod = (
      <>
        {labelInfo}
        {enableSyncControl && (
          <SyncIndicator translate={translate} fieldName={input.name} disabled={disabled} onSyncChange={onSyncChange} />
        )}
      </>
    )

    const modules = {
      toolbar: {
        container: `#${editorId}`,
        handlers: {
          emoji: this.emojiHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }

    const classname = classNames('html-editor', { 'more-lines': hasMoreLines, required: Boolean(labelInfo) })

    return (
      <FormGroup
        className={classname}
        helperText={helperTextMessage}
        label={labelText}
        labelFor={id}
        labelInfo={reqLabelMod}
        intent={intentType}
        inline={inline}
        disabled={disabled}
      >
        <HtmlToolbar
          toolbarId={editorId}
          isSmallEditor={isSmallEditor}
          onEmojiSelect={this.handleEmojiSelect}
          onStripHtml={this.handleStripHtml}
          translate={translate}
        />

        <div>
          <ReactQuill
            theme="snow"
            id={id}
            placeholder={placeholder}
            value={value}
            onChange={this.onQuillChange}
            ref={this.quillEditor}
            modules={modules}
          />
        </div>
      </FormGroup>
    )
  }

  componentDidMount() {
    this.resolveAutofocus()
  }

  private onQuillChange = (inputValue: any) => {
    const { onChange } = this.props.input
    let newValue: string = inputValue

    // removing tags and attributes for successful format validation
    // remove all invalid tags
    const htmlTagRegex = /<(?!\/?(br|hr|p|strong|em|s|blockquote|ol|li|ul|span|div|h1|h2|h3|h4|h5|h6|table|tbody|thead|tfoot|tr|th|td|article)).*?>/g
    newValue = newValue.replace(htmlTagRegex, '')

    // remove style attributes
    const styleRegex = /style=("|').*?("|')/g
    newValue = newValue.replace(styleRegex, '')

    newValue = removalOfNotSupportedCharacters(newValue)

    onChange(newValue)
  }

  private emojiHandler = () => {
    return
  }

  private resolveAutofocus() {
    const { autoFocus = false } = this.props
    if (autoFocus && isPresent(this.quillEditor.current)) {
      this.quillEditor.current.focus()
    }
  }

  private handleEmojiSelect = (emoji: string) => {
    if (isPresent(this.quillEditor.current)) {
      const editor = this.quillEditor.current.getEditor()
      const range = editor.getSelection()
      const cursorPosition = range ? range.index : 0

      editor.insertText(cursorPosition, emoji)
    }
  }

  private handleStripHtml = () => {
    if (isBlank(this.quillEditor.current)) return

    const currentEditor = this.quillEditor.current

    const payload: fromStore.StripHtmlPayload = {
      html: currentEditor.props.value,
      complete: (newValue: string) => currentEditor.setEditorContents(currentEditor.getEditor(), newValue),
    }
    this.props.dispatch(new fromStore.sharedActions.StripHtmlFormAction(payload))
  }
}

export default enhance(HtmlEditorAdapter)



// WEBPACK FOOTER //
// ./src/modules/forms/html-editor-adapter.tsx